import React, { useState, useEffect, useRef } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';

const VideoCall = ({ 
  sessionId, 
  trainerId, 
  clientId, 
  onSessionEnd, 
  onBackToClientPortal,
  sessionOptions = {},
  durationMinutes = null,  // Total paid duration in minutes
  petInfo = null,  // Pet information for trainer view
  lastSessionInfo = null,  // Last session info for trainer view
  sessionStartedAt = null  // Session start time from backend (ISO string)
}) => {
  const [isJoined, setIsJoined] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [sessionTime, setSessionTime] = useState(0);
  const [sessionStatus, setSessionStatus] = useState('connecting');
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isTimeUp, setIsTimeUp] = useState(false);
  
  const clientRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const sessionTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const autoEndTimerRef = useRef(null);

  // Agora configuration - fetch from backend
  const [agoraConfig, setAgoraConfig] = useState(null);
  const CHANNEL = `session_${sessionId}`;

  useEffect(() => {
    fetchAgoraConfig();
  }, []);

  useEffect(() => {
    if (agoraConfig && agoraConfig.appId) {
      initializeAgora();
      startSessionTimer();
    }
    
    return () => {
      cleanup();
    };
  }, [agoraConfig]);

  const fetchAgoraConfig = async () => {
    try {
      setError(null);
      setSessionStatus('initializing');
      
      // Generate a unique UID for this user
      const uid = Math.floor(Math.random() * 1000000).toString();
      
      // Fetch Agora config from backend
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/agora/token?channel=${encodeURIComponent(CHANNEL)}&uid=${uid}&sessionId=${sessionId}`
      );
      
      const data = await response.json();
      
      if (data.Code === 500 || !data.appId) {
        throw new Error(data.Message || 'Failed to get Agora configuration. Please ensure AGORA_APP_ID is set in the backend environment.');
      }
      
      // Handle token - convert null/undefined to empty string for static key mode
      const token = data.token !== null && data.token !== undefined ? data.token : "";
      
      console.log('Agora config received:', {
        appId: data.appId,
        tokenLength: token ? token.length : 0,
        tokenPreview: token ? token.substring(0, 20) + '...' : 'empty',
        channel: data.channel || CHANNEL,
        uid: uid
      });
      
      setAgoraConfig({
        appId: data.appId,
        token: token,
        channel: data.channel || CHANNEL,
        uid: uid
      });
      
    } catch (err) {
      console.error('Failed to fetch Agora config:', err);
      setError(`Failed to connect to video call: ${err.message}`);
      setSessionStatus('error');
    }
  };

  const initializeAgora = async () => {
    if (!agoraConfig || !agoraConfig.appId) {
      setError('Agora App ID not available');
      setSessionStatus('error');
      return;
    }
    
    try {
      setError(null);
      setSessionStatus('initializing');
      
      // Create Agora client
      clientRef.current = AgoraRTC.createClient({ 
        mode: "rtc", 
        codec: "vp8" 
      });

      // Set up event handlers
      clientRef.current.on("user-published", handleUserPublished);
      clientRef.current.on("user-unpublished", handleUserUnpublished);
      clientRef.current.on("user-left", handleUserLeft);
      clientRef.current.on("user-joined", handleUserJoined);

      // Join the channel
      await clientRef.current.join(agoraConfig.appId, agoraConfig.channel, agoraConfig.token, agoraConfig.uid);
      setIsJoined(true);
      setSessionStatus('active');
      
      // Publish local streams
      await publishLocalStreams();
      
    } catch (err) {
      console.error('Failed to initialize Agora:', err);
      setError(`Failed to connect to video call: ${err.message}`);
      setSessionStatus('error');
    }
  };

  const publishLocalStreams = async () => {
    try {
      // Create and publish local video stream
      const localVideoStream = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: {
          width: 1280,
          height: 720,
          frameRate: 30
        }
      });
      localVideoTrackRef.current = localVideoStream;
      await clientRef.current.publish(localVideoStream);
      
      if (localVideoRef.current) {
        localVideoStream.play(localVideoRef.current);
      }

      // Create and publish local audio stream
      const localAudioStream = await AgoraRTC.createMicrophoneAudioTrack({
        encoderConfig: {
          sampleRate: 48000,
          stereo: true,
          bitrate: 48
        }
      });
      localAudioTrackRef.current = localAudioStream;
      await clientRef.current.publish(localAudioStream);
      
      // Don't play local audio back (no echo)
      console.log('Local audio track created and published');

    } catch (err) {
      console.error('Failed to publish local streams:', err);
      setError('Failed to start camera/microphone: ' + err.message);
    }
  };

  const handleUserPublished = async (user, mediaType) => {
    try {
      await clientRef.current.subscribe(user, mediaType);
      
      if (mediaType === "video") {
        const remoteVideoStream = user.videoTrack;
        if (remoteVideoRef.current && remoteVideoStream) {
          remoteVideoStream.play(remoteVideoRef.current);
        }
      }
      
      if (mediaType === "audio") {
        const remoteAudioStream = user.audioTrack;
        if (remoteAudioStream) {
          // Play remote audio directly - Agora handles audio playback automatically
          remoteAudioStream.play();
          console.log('Remote audio track playing');
        }
      }

      setParticipants(prev => {
        const exists = prev.find(p => p.uid === user.uid);
        if (!exists) {
          return [...prev, user];
        }
        return prev;
      });
    } catch (err) {
      console.error('Failed to subscribe to user:', err);
    }
  };

  const handleUserUnpublished = (user, mediaType) => {
    console.log('User unpublished:', user, mediaType);
  };

  const handleUserLeft = (user) => {
    console.log('User left:', user);
    setParticipants(prev => prev.filter(p => p.uid !== user.uid));
  };

  const handleUserJoined = (user) => {
    console.log('User joined:', user);
  };

  const toggleMute = async () => {
    try {
      if (localAudioTrackRef.current) {
        const newMutedState = !isMuted;
        await localAudioTrackRef.current.setEnabled(!newMutedState);
        setIsMuted(newMutedState);
        console.log(`Audio ${newMutedState ? 'muted' : 'unmuted'}`);
      } else {
        console.warn('Local audio track not found');
      }
    } catch (err) {
      console.error('Failed to toggle mute:', err);
    }
  };

  const toggleVideo = async () => {
    try {
      if (localVideoTrackRef.current) {
        const newVideoState = !isVideoOn;
        await localVideoTrackRef.current.setEnabled(!newVideoState);
        setIsVideoOn(newVideoState);
        console.log(`Video ${newVideoState ? 'turned off' : 'turned on'}`);
      } else {
        console.warn('Local video track not found');
      }
    } catch (err) {
      console.error('Failed to toggle video:', err);
    }
  };


  const startSessionTimer = () => {
    // Use session_started_at from backend if available, otherwise use current time
    let sessionStartTime;
    if (sessionStartedAt) {
      try {
        sessionStartTime = new Date(sessionStartedAt).getTime();
        console.log('Using session start time from backend:', new Date(sessionStartTime).toISOString());
      } catch (e) {
        console.warn('Failed to parse sessionStartedAt, using current time:', e);
        sessionStartTime = Date.now();
      }
    } else {
      sessionStartTime = Date.now();
      console.log('No session start time provided, using current time');
    }
    
    startTimeRef.current = sessionStartTime;
    const durationSeconds = durationMinutes ? durationMinutes * 60 : null;
    
    sessionTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setSessionTime(elapsed);
      
      // Calculate remaining time if duration is set
      if (durationSeconds) {
        const remaining = Math.max(0, durationSeconds - elapsed);
        setTimeRemaining(remaining);
        
        // Auto-end when time is up
        if (remaining === 0 && !isTimeUp) {
          setIsTimeUp(true);
          // Give a 5 second warning before auto-ending
          setTimeout(() => {
            endSession();
          }, 5000);
        }
      }
    }, 1000);
    
    // Set up auto-end timer if duration is specified
    if (durationSeconds) {
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remainingSeconds = Math.max(0, durationSeconds - elapsedSeconds);
      
      if (remainingSeconds > 0) {
        autoEndTimerRef.current = setTimeout(() => {
          setIsTimeUp(true);
          // Show warning for 5 seconds before ending
          setTimeout(() => {
            endSession();
          }, 5000);
        }, remainingSeconds * 1000);
      }
    }
  };

  const endSession = async () => {
    try {
      setSessionStatus('ending');
      
      // Stop timer
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }

      // Leave the channel
      if (clientRef.current) {
        await clientRef.current.leave();
      }

      // Clean up
      cleanup();
      
      // Notify parent component
      onSessionEnd({
        sessionId,
        duration: sessionTime,
        endTime: new Date().toISOString(),
        status: 'completed'
      });
      
    } catch (err) {
      console.error('Failed to end session:', err);
      setError('Failed to end session properly');
    }
  };

  const cleanup = () => {
    // Stop and close local tracks
    if (localVideoTrackRef.current) {
      localVideoTrackRef.current.stop();
      localVideoTrackRef.current.close();
      localVideoTrackRef.current = null;
    }
    if (localAudioTrackRef.current) {
      localAudioTrackRef.current.stop();
      localAudioTrackRef.current.close();
      localAudioTrackRef.current = null;
    }
    
    if (clientRef.current) {
      clientRef.current.removeAllListeners();
    }
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
    }
    if (autoEndTimerRef.current) {
      clearTimeout(autoEndTimerRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
      minHeight: '100vh',
      color: 'white',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      background: 'rgba(0, 0, 0, 0.8)',
      padding: '15px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backdropFilter: 'blur(10px)'
    },
    sessionInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px'
    },
    sessionId: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#f39c12'
    },
    timer: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: '#2ecc71',
      fontFamily: 'monospace'
    },
    status: {
      padding: '5px 15px',
      borderRadius: '20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      background: sessionStatus === 'active' ? '#2ecc71' : 
                  sessionStatus === 'error' ? '#e74c3c' : '#f39c12',
      color: 'white'
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      padding: '20px',
      gap: '20px'
    },
    videoContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    videoGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px',
      flex: 1
    },
    videoBox: {
      background: '#000',
      borderRadius: '10px',
      overflow: 'hidden',
      position: 'relative',
      minHeight: '300px'
    },
    videoElement: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    videoLabel: {
      position: 'absolute',
      top: '10px',
      left: '10px',
      background: 'rgba(0, 0, 0, 0.7)',
      color: 'white',
      padding: '5px 10px',
      borderRadius: '5px',
      fontSize: '0.9rem',
      fontWeight: '600'
    },
    controls: {
      display: 'flex',
      justifyContent: 'center',
      gap: '15px',
      padding: '20px',
      background: 'rgba(0, 0, 0, 0.8)',
      borderRadius: '10px',
      backdropFilter: 'blur(10px)'
    },
    controlButton: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      transition: 'all 0.3s ease',
      fontWeight: '600'
    },
    muteButton: {
      background: isMuted ? '#e74c3c' : '#34495e',
      color: 'white'
    },
    videoButton: {
      background: isVideoOn ? '#34495e' : '#e74c3c',
      color: 'white'
    },
    endButton: {
      background: '#e74c3c',
      color: 'white',
      width: '80px',
      height: '60px',
      borderRadius: '30px'
    },
    sidebar: {
      width: '300px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '10px',
      padding: '20px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    sidebarTitle: {
      fontSize: '1.2rem',
      fontWeight: '700',
      marginBottom: '20px',
      color: '#f39c12'
    },
    participantList: {
      marginBottom: '20px'
    },
    participantItem: {
      padding: '10px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      marginBottom: '10px',
      fontSize: '0.9rem'
    },
    sessionNotes: {
      marginBottom: '20px'
    },
    notesTextarea: {
      width: '100%',
      height: '100px',
      padding: '10px',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      fontSize: '0.9rem',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    error: {
      background: 'rgba(231, 76, 60, 0.2)',
      border: '1px solid #e74c3c',
      color: '#e74c3c',
      padding: '15px',
      borderRadius: '10px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    loading: {
      textAlign: 'center',
      color: '#f39c12',
      fontSize: '1.1rem',
      padding: '40px'
    }
  };

  if (sessionStatus === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.sessionInfo}>
            <span style={styles.sessionId}>Session {sessionId}</span>
            <span style={styles.status}>Error</span>
          </div>
        </div>
        <div style={styles.loading}>
          <div style={styles.error}>{error}</div>
          <button
            onClick={onBackToClientPortal}
            style={{
              background: 'linear-gradient(45deg, #e67e22, #f39c12)',
              color: 'white',
              border: 'none',
              padding: '15px 30px',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Back to Client Portal
          </button>
        </div>
      </div>
    );
  }

  if (sessionStatus === 'connecting' || sessionStatus === 'initializing') {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.sessionInfo}>
            <span style={styles.sessionId}>Session {sessionId}</span>
            <span style={styles.status}>Connecting...</span>
          </div>
        </div>
        <div style={styles.loading}>
          <div>🔗 Connecting to video call...</div>
          <div style={{ fontSize: '0.9rem', marginTop: '10px', color: 'rgba(255, 255, 255, 0.7)' }}>
            Please allow camera and microphone access when prompted
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.sessionInfo}>
          <span style={styles.sessionId}>Session {sessionId}</span>
          <span style={styles.timer}>
            {formatTime(sessionTime)}
            {timeRemaining !== null && (
              <span style={{ 
                fontSize: '0.9rem', 
                marginLeft: '10px',
                color: timeRemaining < 300 ? '#e74c3c' : '#2ecc71' 
              }}>
                ({formatTime(timeRemaining)} remaining)
              </span>
            )}
          </span>
          <span style={styles.status}>{sessionStatus}</span>
        </div>
        {isTimeUp && (
          <div style={{
            background: '#e74c3c',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600'
          }}>
            ⚠️ Session time is up! Ending in 5 seconds...
          </div>
        )}
        <button
          onClick={onBackToClientPortal}
          style={{
            background: 'transparent',
            border: '2px solid #e74c3c',
            color: '#e74c3c',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          Exit Session
        </button>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Video Area */}
        <div style={styles.videoContainer}>
          <div style={styles.videoGrid}>
            {/* Local Video */}
            <div style={styles.videoBox}>
              <div style={styles.videoLabel}>You</div>
              <div
                ref={localVideoRef}
                style={styles.videoElement}
              />
            </div>

            {/* Remote Video */}
            <div style={styles.videoBox}>
              <div style={styles.videoLabel}>Trainer</div>
              <div
                ref={remoteVideoRef}
                style={styles.videoElement}
              />
            </div>
          </div>

          {/* Controls */}
          <div style={styles.controls}>
            <button
              onClick={toggleMute}
              style={{...styles.controlButton, ...styles.muteButton}}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? '🔇' : '🎤'}
            </button>
            
            <button
              onClick={toggleVideo}
              style={{...styles.controlButton, ...styles.videoButton}}
              title={isVideoOn ? 'Turn off video' : 'Turn on video'}
            >
              {isVideoOn ? '📹' : '📷'}
            </button>
            
            <button
              onClick={endSession}
              style={{...styles.controlButton, ...styles.endButton}}
              title="End session"
            >
              📞
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div style={styles.sidebar}>
          <h3 style={styles.sidebarTitle}>Session Info</h3>
          
          {/* Trainer-specific sidebar: Pet info and last session */}
          {trainerId && petInfo && (
            <>
              <div style={{ 
                marginBottom: '20px', 
                padding: '15px', 
                background: 'rgba(46, 204, 113, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(46, 204, 113, 0.3)'
              }}>
                <h4 style={{ fontSize: '1rem', marginBottom: '10px', color: '#2ecc71' }}>
                  Pet Information
                </h4>
                <div style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                  <div><strong>Name:</strong> {petInfo.pet_name || 'N/A'}</div>
                  <div><strong>Breed:</strong> {petInfo.race || 'N/A'}</div>
                  <div><strong>Age:</strong> {petInfo.age ? `${petInfo.age} years` : 'N/A'}</div>
                  {petInfo.target_behaviors && (
                    <div style={{ marginTop: '8px' }}>
                      <strong>Target Behaviors:</strong> {petInfo.target_behaviors}
                    </div>
                  )}
                </div>
              </div>

              {lastSessionInfo && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '15px', 
                  background: 'rgba(243, 156, 18, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(243, 156, 18, 0.3)'
                }}>
                  <h4 style={{ fontSize: '1rem', marginBottom: '10px', color: '#f39c12' }}>
                    Last Session Summary
                  </h4>
                  <div style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                    {lastSessionInfo.session_summary ? (
                      <div style={{ marginBottom: '8px' }}>
                        {lastSessionInfo.session_summary.substring(0, 200)}
                        {lastSessionInfo.session_summary.length > 200 ? '...' : ''}
                      </div>
                    ) : (
                      <div style={{ color: 'rgba(255, 255, 255, 0.6)' }}>No previous session data</div>
                    )}
                    {lastSessionInfo.training_tasks && (
                      <div style={{ marginTop: '8px' }}>
                        <strong>Previous Tasks:</strong>
                        <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                          {JSON.parse(lastSessionInfo.training_tasks || '[]').slice(0, 3).map((task, idx) => (
                            <li key={idx}>{task}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          
          <div style={styles.participantList}>
            <h4 style={{ fontSize: '1rem', marginBottom: '10px', color: '#f39c12' }}>
              Participants ({participants.length + 1})
            </h4>
            <div style={styles.participantItem}>
              {trainerId ? 'You (Trainer)' : 'You (Client)'}
            </div>
            {participants.map((participant, index) => (
              <div key={participant.uid} style={styles.participantItem}>
                {trainerId ? 'Client' : 'Trainer'} {index + 1}
              </div>
            ))}
          </div>

          <div style={styles.sessionNotes}>
            <h4 style={{ fontSize: '1rem', marginBottom: '10px', color: '#f39c12' }}>
              Session Notes
            </h4>
            <textarea
              style={styles.notesTextarea}
              placeholder="Add notes about this session..."
            />
          </div>

          {sessionOptions.notes && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '1rem', marginBottom: '10px', color: '#f39c12' }}>
                Session Goals
              </h4>
              <div style={{
                background: 'rgba(243, 156, 18, 0.1)',
                border: '1px solid rgba(243, 156, 18, 0.3)',
                borderRadius: '8px',
                padding: '10px',
                fontSize: '0.9rem'
              }}>
                {sessionOptions.notes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCall;


